import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { IAnalyticsService } from '@core/interfaces/IAnalyticsService.ts';

interface PageInfo {
    url: string;
    name: string;
}
interface PageInfo {
    url: string;
    name: string;
}

interface QueuedOperation {
    type: 'start' | 'stop' | 'event' | 'exception';
    data: any;
    timestamp: number;
}

export class AnalyticsService implements IAnalyticsService {
    private appInsights: ApplicationInsights | null = null;
    private currentPageInfo: PageInfo | null = null;
    private previousPageInfo: PageInfo | null = null;
    private _isInitialized = false;

    // Performance optimizations
    private operationQueue: QueuedOperation[] = [];
    private processingQueue = false;
    private flushInterval: number | null = null;

    constructor() {
        console.log('[AnalyticsService] Service created');
    }

    isInitialized(): boolean {
        return this._isInitialized;
    }

    initialize(): void {
        if (this._isInitialized) {
            // Already initialized, ignore
            return;
        }

        const instrumentationKey = import.meta.env.VITE_INSTRUMENTATIONKEY;

        if (!instrumentationKey) {
            console.warn('[AnalyticsService] Instrumentation key not found. Analytics disabled.');
            return;
        }

        try {
            this.appInsights = new ApplicationInsights({
                config: {
                    instrumentationKey,
                    enableAutoRouteTracking: false,
                    loggingLevelConsole: 0, // ✅ Reduce console logging
                    loggingLevelTelemetry: 2,
                    autoTrackPageVisitTime: true,
                    disableFetchTracking: false,
                    enableCorsCorrelation: true,
                    enableRequestHeaderTracking: false, // ✅ Reduce overhead
                    enableResponseHeaderTracking: false, // ✅ Reduce overhead
                    disableExceptionTracking: false,
                    maxBatchInterval: 5000, // ✅ Batch telemetry every 5 seconds
                    maxBatchSizeInBytes: 102400, // ✅ 100KB batch size
                    disableTelemetry: false,
                    samplingPercentage: 100 // Adjust if needed (lower = less data sent)
                }
            });

            this.appInsights.loadAppInsights();
            this._isInitialized = true;

            // ✅ Setup periodic flush in background
            this.setupPeriodicFlush();
        } catch (error) {
            console.error('[AnalyticsService] Failed to initialize:', error);
            this.appInsights = null;
            this._isInitialized = false;
        }
    }

    /**
     * Setup periodic flush to send batched telemetry
     */
    private setupPeriodicFlush(): void {
        if (this.flushInterval) return;

        // Flush every 10 seconds in background
        this.flushInterval = window.setInterval(() => {
            if ('requestIdleCallback' in window) {
                requestIdleCallback(() => this.flush());
            } else {
                this.flush();
            }
        }, 10000);
    }

    /**
     * Process queued operations in background
     */
    private processQueue(): void {
        if (this.processingQueue || this.operationQueue.length === 0) {
            return;
        }

        this.processingQueue = true;

        if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
                this.executeQueuedOperations();
            });
        } else {
            setTimeout(() => {
                this.executeQueuedOperations();
            }, 0);
        }
    }

    private executeQueuedOperations(): void {
        while (this.operationQueue.length > 0) {
            const operation = this.operationQueue.shift();
            if (!operation) break;

            try {
                switch (operation.type) {
                    case 'start':
                        this.executeStartTrackPage(operation.data.name);
                        break;
                    case 'stop':
                        this.executeStopTrackPage(operation.data.name, operation.data.url);
                        break;
                    case 'event':
                        this.executeTrackEvent(
                            operation.data.name,
                            operation.data.properties,
                            operation.data.measurements
                        );
                        break;
                    case 'exception':
                        this.executeTrackException(operation.data.error, operation.data.properties);
                        break;
                }
            } catch (error) {
                console.error('[AnalyticsService] Error processing queue:', error);
            }
        }

        this.processingQueue = false;
    }

    /**
     * Non-blocking startTrackPage
     */
    startTrackPage(name: string, url: string): void {
        if (!this.appInsights || !this._isInitialized) return;

        // Queue the operation instead of executing immediately
        this.operationQueue.push({
            type: 'start',
            data: { name, url },
            timestamp: Date.now()
        });

        // Update state immediately (synchronous)
        this.currentPageInfo = { url, name };
        this.previousPageInfo = { url, name };

        // Process queue in background
        this.processQueue();
    }

    private executeStartTrackPage(name: string): void {
        if (!this.appInsights) return;

        try {
            this.appInsights.startTrackPage(name);
        } catch (error) {
            console.error('[AnalyticsService] Error starting page track:', error);
        }
    }

    /**
     * Non-blocking stopTrackPage
     */
    stopTrackPage(name: string, url: string): void {
        if (!this.appInsights || !this._isInitialized) return;

        // Queue the operation
        this.operationQueue.push({
            type: 'stop',
            data: { name, url },
            timestamp: Date.now()
        });

        this.processQueue();
    }

    private executeStopTrackPage(name: string, url: string): void {
        if (!this.appInsights) return;

        try {
            this.appInsights.stopTrackPage(name, url);
        } catch (error) {
            console.error('[AnalyticsService] Error stopping page track:', error);
        }
    }

    /**
     * Non-blocking trackPageView
     */
    trackPageView(name?: string, uri?: string, properties?: Record<string, any>): void {
        if (!this.appInsights || !this._isInitialized) return;

        if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
                this.appInsights?.trackPageView({ name, uri, properties });
            });
        } else {
            setTimeout(() => {
                this.appInsights?.trackPageView({ name, uri, properties });
            }, 0);
        }
    }

    /**
     * Non-blocking trackEvent
     */
    trackEvent(name: string, properties?: Record<string, any>, measurements?: Record<string, number>): void {
        if (!this.appInsights || !this._isInitialized) return;

        this.operationQueue.push({
            type: 'event',
            data: { name, properties, measurements },
            timestamp: Date.now()
        });

        this.processQueue();
    }

    private executeTrackEvent(
        name: string,
        properties?: Record<string, any>,
        measurements?: Record<string, number>
    ): void {
        if (!this.appInsights) return;

        try {
            this.appInsights.trackEvent({ name, properties, measurements });
        } catch (error) {
            console.error('[AnalyticsService] trackEvent failed:', error);
        }
    }

    /**
     * Non-blocking trackException
     */
    trackException(error: Error | unknown, properties?: Record<string, any>): void {
        if (!this.appInsights || !this._isInitialized) return;

        const exception = error instanceof Error ? error : new Error(String(error));

        this.operationQueue.push({
            type: 'exception',
            data: { error: exception, properties },
            timestamp: Date.now()
        });

        this.processQueue();
    }

    private executeTrackException(error: Error, properties?: Record<string, any>): void {
        if (!this.appInsights) return;

        try {
            this.appInsights.trackException({
                exception: error,
                properties,
                severityLevel: 3
            });
        } catch (e) {
            console.error('[AnalyticsService] trackException failed:', e);
        }
    }

    trackMetric(name: string, average: number, properties?: Record<string, any>): void {
        if (!this.appInsights || !this._isInitialized) return;

        if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
                this.appInsights?.trackMetric({ name, average }, properties);
            });
        } else {
            setTimeout(() => {
                this.appInsights?.trackMetric({ name, average }, properties);
            }, 0);
        }
    }

    setAuthenticatedUserContext(userId: string, accountId?: string): void {
        if (!this.appInsights || !this._isInitialized) return;

        try {
            this.appInsights.setAuthenticatedUserContext(userId, accountId);
        } catch (error) {
            console.error('[AnalyticsService] setAuthenticatedUserContext failed:', error);
        }
    }

    clearAuthenticatedUserContext(): void {
        if (!this.appInsights || !this._isInitialized) return;

        try {
            this.appInsights.clearAuthenticatedUserContext();
        } catch (error) {
            console.error('[AnalyticsService] clearAuthenticatedUserContext failed:', error);
        }
    }

    getCurrentPage(): PageInfo | null {
        return this.currentPageInfo;
    }

    getInstance(): ApplicationInsights | null {
        return this.appInsights;
    }

    flush(): void {
        if (!this.appInsights || !this._isInitialized) return;

        try {
            this.appInsights.flush();
        } catch (error) {
            console.error('[AnalyticsService] flush failed:', error);
        }
    }

    cleanup(): void {
        // Clear flush interval
        if (this.flushInterval) {
            clearInterval(this.flushInterval);
            this.flushInterval = null;
        }

        // Process remaining queue
        this.executeQueuedOperations();

        // Stop tracking and flush
        if (this.previousPageInfo) {
            this.executeStopTrackPage(this.previousPageInfo.name, this.previousPageInfo.url);
        }

        this.flush();
    }
}
